import React, { useState, useEffect, useMemo, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useLocation } from "react-router-dom";
import { postBackendHelper } from "../../utils/backendHelperApi";
import CTextField from "../../components/CTextField";
import CDataGrid from "../../components/CDataGrid";
import CButton from "../../components/CButton";
import CTabs, { CTabPanel } from "../../components/CTabs";
import SearchIcon from "@mui/icons-material/Search";
import AddIcon from "@mui/icons-material/Add";
import FilterListIcon from "@mui/icons-material/FilterList";
import { ReactComponent as DeleteIcon } from "../../assets/images/trash-icon.svg";
import {
  Box,
  Popover,
  Checkbox,
  FormControlLabel,
  IconButton,
  Tooltip,
} from "@mui/material";
import { fromWithCompany, supabase } from "../../config/supabaseClient";
import { getLocalData } from "../../constant/utils/commonUtils";
import { fetchOrganogram } from "../../slices/organogramSlice";
import { toast } from "react-toastify";
import CLoader from "../../components/CLoader";
import {
  CHAT_CATEGORIES,
  PROGRAM_DURATION_OPTIONS,
} from "../../constant/constants";
import { DEFAULT_PROMPTS } from "../../constant/prompt";
import ConfirmationDialog from "../../components/ConfirmationDialog";
import CDialog from "../../components/CDialog";
import CloseIcon from "@mui/icons-material/Close";

/**
 * Remove training request IDs from grouped rows when status is no longer "requested"
 * (e.g. processed after catalogue assignment, or rejected).
 */
function pruneGroupedRowsByRequestStatus(groupRows, statusById) {
  if (!groupRows?.length) return groupRows;
  return groupRows
    .map((row) => {
      const ids = row.trainingRequestIds || [];
      const remaining = ids.filter((id) => {
        const st = statusById.get(id);
        if (st === undefined) return true;
        return st === "requested";
      });
      if (remaining.length === 0) return null;
      if (remaining.length === ids.length) return row;
      return {
        ...row,
        trainingRequestIds: remaining,
        count: remaining.length,
      };
    })
    .filter(Boolean);
}

const RequestedCatalogue = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchQuery, setSearchQuery] = useState("");
  const [activeTab, setActiveTab] = useState("Requests");
  const [requestListRows, setRequestListRows] = useState([]);
  const [groupRequestListRows, setGroupRequestListRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [groupingWithAI, setGroupingWithAI] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [rowToDelete, setRowToDelete] = useState(null);
  const [learnersDialogOpen, setLearnersDialogOpen] = useState(false);
  const [selectedGroupLearners, setSelectedGroupLearners] = useState([]);
  const trainingAddedHandledRef = useRef(false);

  // Excel-style column filters
  const [columnFilters, setColumnFilters] = useState({});
  const [filterAnchorEl, setFilterAnchorEl] = useState(null);
  const [activeFilterColumn, setActiveFilterColumn] = useState(null);
  const dispatch = useDispatch();
  const organogram = useSelector((state) => state.organogram.data);
  const organogramLoading = useSelector((state) => state.organogram.loading);

  const tabs = [
    { label: "Requests", value: "Requests" },
    { label: "Grouped Requests", value: "Grouped Requests" },
  ];

  // Columns for Requests
  const requestListColumns = [
    {
      field: "employeeName",
      headerName: "Employee Name",
      minWidth: 150,
      flex: 1,
    },
    {
      field: "department",
      headerName: "Department",
      minWidth: 150,
      flex: 1,
      renderHeader: () => renderHeaderWithFilter("department", "Department"),
    },
    {
      field: "trainingName",
      headerName: "Training Name",
      minWidth: 200,
      flex: 1,
    },
    {
      field: "program_duration",
      headerName: "Program Duration",
      minWidth: 200,
      flex: 1,
      renderCell: ({ value }) => {
        if (!value || value === "-") return "-";
        const option = PROGRAM_DURATION_OPTIONS.find((opt) => opt.id === value);
        return option ? option.value : value;
      },
      renderHeader: () =>
        renderHeaderWithFilter("program_duration", "Program Duration"),
    },
    {
      field: "motivation",
      headerName: "Motivation",
      minWidth: 250,
      flex: 1,
      renderCell: ({ value }) => (
        <div className="truncate max-w-[300px]" title={value || "-"}>
          {value || "-"}
        </div>
      ),
    },
    {
      field: "status",
      headerName: "Status",
      minWidth: 150,
      flex: 1,
      renderCell: ({ row }) => {
        // Show status based on status field: processed, rejected, or requested
        const status = row.status;
        let statusText = "Requested";
        let statusColor = "text-gray-600";

        if (status === "processed") {
          statusText = "Processed";
          statusColor = "text-green-600 font-semibold";
        } else if (status === "rejected") {
          statusText = "Rejected";
          statusColor = "text-red-600 font-semibold";
        }

        return <span className={statusColor}>{statusText}</span>;
      },
      renderHeader: () => renderHeaderWithFilter("status", "Status"),
    },
    {
      field: "actions",
      headerName: "Actions",
      minWidth: 120,
      flex: 1,
      sortable: false,
      renderCell: ({ row }) => {
        // Only show reject icon for requested status
        if (row.status === "requested") {
          return (
            <Tooltip title="Reject Request" arrow>
              <CloseIcon
                onClick={() => handleRejectRequest(row.id)}
                className="text-red-500 hover:text-red-700 cursor-pointer"
                sx={{ fontSize: 20 }}
              />
            </Tooltip>
          );
        }
        return <span className="text-gray-400">-</span>;
      },
    },
  ];

  // Columns for Grouped Requests
  const groupRequestListColumns = [
    {
      field: "topic",
      headerName: "Topic",
      minWidth: 200,
      flex: 1,
    },
    {
      field: "trainingName",
      headerName: "Training Name",
      minWidth: 200,
      flex: 1,
    },
    {
      field: "count",
      headerName: "Count",
      width: 120,
      align: "center",
      headerAlign: "center",
      renderCell: ({ row, value }) => {
        // Show count based on actual training request IDs (source of truth)
        const actualCount = row.trainingRequestIds?.length || 0;
        return (
          <div className="flex items-center justify-center w-full h-full">
            <span
              className="text-blue-500 hover:text-blue-700 cursor-pointer underline font-semibold"
              onClick={(e) => {
                e.stopPropagation();
                handleCountClick(row);
              }}
              title="Click to view learners"
            >
              {actualCount}
            </span>
          </div>
        );
      },
    },
    {
      field: "motivation",
      headerName: "Motivation",
      minWidth: 250,
      flex: 1,
      renderCell: ({ value }) => (
        <div className="truncate max-w-[300px]" title={value || "-"}>
          {value || "-"}
        </div>
      ),
    },
    {
      field: "program_duration",
      headerName: "Program Duration",
      minWidth: 200,
      flex: 1,
      renderCell: ({ value }) => {
        if (!value || value === "-") return "-";
        const option = PROGRAM_DURATION_OPTIONS.find((opt) => opt.id === value);
        return option ? option.value : value;
      },
    },
    {
      field: "actions",
      headerName: "Actions",
      width: 120,
      sortable: false,
      renderCell: (params) => (
        <div className="flex 2xl:gap-3 gap-2 items-center h-full">
          <CButton
            tooltip="Add"
            tooltipPosition="top"
            onClick={(e) => {
              e.stopPropagation();
              handleAddTraining(params.row);
            }}
            className="!min-w-0 bg-blue-100 text-blue-600 hover:bg-blue-200 2xl:!h-[28px] 2xl:!w-[28px] !h-[24px] !w-[24px] !p-0 !rounded-[5px] inline-flex items-center justify-center"
          >
            <AddIcon
              className="!fill-blue-500 !text-blue-500"
              fontSize="small"
            />
          </CButton>
          <CButton
            tooltip="Delete"
            tooltipPosition="top"
            onClick={(e) => {
              e.stopPropagation();
              handleDeleteClick(params.row);
            }}
            className="!min-w-0 bg-red-100 text-red-600 hover:bg-red-200 2xl:!h-[28px] 2xl:!w-[28px] !h-[24px] !w-[24px] !p-0 !rounded-[5px] inline-flex items-center justify-center"
          >
            <DeleteIcon className="!fill-red-500 !text-red-500" />
          </CButton>
        </div>
      ),
    },
  ];

  // Fetch organogram data if not already loaded
  useEffect(() => {
    if (!organogram?.length && !organogramLoading) {
      const localData = getLocalData();
      if (localData?.company_id) {
        dispatch(fetchOrganogram(localData.company_id));
      }
    }
  }, [dispatch]);

  // Store raw training requests data
  const [rawTrainingRequests, setRawTrainingRequests] = useState([]);

  // Load grouped results from localStorage on component mount (persists until logout)
  useEffect(() => {
    const loadGroupedResults = () => {
      try {
        const localData = getLocalData();
        if (!localData?.company_id) return;

        const storageKey = `groupedTrainingRequests_${localData.company_id}`;
        const storedData = localStorage.getItem(storageKey);

        if (storedData) {
          const parsedData = JSON.parse(storedData);
          setGroupRequestListRows(parsedData);
        }
      } catch (error) {
        console.error("Error loading grouped results from storage:", error);
      }
    };

    loadGroupedResults();
  }, []);

  // Fetch training requests
  useEffect(() => {
    const fetchTrainingRequests = async () => {
      setLoading(true);
      try {
        const localData = getLocalData();
        if (!localData?.company_id) {
          toast.error("Company ID not found");
          setLoading(false);
          return;
        }

        // Fetch training requests with user details
        // Fetch all requests (both requested and processed) to show status

        const { data: trainingRequests, error: requestsError } =
          await fromWithCompany("training_requests")
            .select(
              `
            *,
            company_user!user_id (
              id,
              first_name,
              last_name,
              department_id,
              email
            )
          `
            )
            .in("status", ["requested", "processed", "rejected"]) // Include requested, processed, and rejected to show status
            .order("created_at", { ascending: false });

        if (trainingRequests && trainingRequests.length > 0) {
        }

        if (requestsError) {
          throw requestsError;
        }

        if (!trainingRequests || trainingRequests.length === 0) {
          setRequestListRows([]);
          setGroupRequestListRows([]);
          setRawTrainingRequests([]);
          setLoading(false);
          return;
        }

        // Store raw data
        setRawTrainingRequests(trainingRequests);
      } catch (error) {
        console.error("Error fetching training requests:", error);
        toast.error("Error fetching training requests: " + error.message);
        setRequestListRows([]);
        setGroupRequestListRows([]);
        setRawTrainingRequests([]);
      } finally {
        setLoading(false);
      }
    };

    fetchTrainingRequests();
  }, []);

  // Map training requests data when organogram is available
  useEffect(() => {
    if (!rawTrainingRequests.length) return;

    // Map the data to match the table columns
    // Include all training requests (both requested and processed) to show status
    const mappedData = rawTrainingRequests
      .filter(
        (request) =>
          request.status === "requested" ||
          request.status === "processed" ||
          request.status === "rejected"
      )
      .map((request) => {
        const user = request.company_user;
        const employeeName = user
          ? `${user.first_name || ""} ${user.last_name || ""}`.trim() ||
            user.email ||
            "Unknown"
          : request.created_by || "Unknown";

        // Get department name from organogram
        let departmentName = "-";
        if (user?.department_id) {
          if (organogram?.length) {
            const dept = organogram.find(
              (org) => org.departmentId === user.department_id
            );
            departmentName = dept ? dept.name : user.department_id;
          } else {
            departmentName = user.department_id;
          }
        }

        return {
          id: request.id,
          employeeName: employeeName,
          department: departmentName,
          trainingName: request.name || "-",
          program_duration: request.program_duration || "-",
          motivation: request.motivation || "-",
          status: request.status, // Use status directly
          originalData: request, // Store original data for reference
        };
      });

    setRequestListRows(mappedData);
    // Don't set groupRequestListRows here - it should only be populated after AI grouping
  }, [rawTrainingRequests, organogram]);

  // Keep grouped cache in sync: drop processed/rejected IDs so Grouped Requests matches Requests tab
  useEffect(() => {
    const statusById = new Map(
      (rawTrainingRequests || []).map((r) => [r.id, r.status]),
    );

    setGroupRequestListRows((prev) => {
      if (!prev?.length) return prev;
      const pruned = pruneGroupedRowsByRequestStatus(prev, statusById);
      if (JSON.stringify(pruned) === JSON.stringify(prev)) return prev;
      try {
        const localData = getLocalData();
        if (localData?.company_id) {
          const storageKey = `groupedTrainingRequests_${localData.company_id}`;
          localStorage.setItem(storageKey, JSON.stringify(pruned));
        }
      } catch (e) {
        console.error("Error persisting pruned grouped requests:", e);
      }
      return pruned;
    });
  }, [rawTrainingRequests]);

  // Get unique values for a column (for Excel-style filter)
  const getUniqueColumnValues = (field) => {
    if (!requestListRows || requestListRows.length === 0) return [];
    const values = new Set();
    requestListRows.forEach((item) => {
      let value = item[field];
      if (value !== null && value !== undefined && value !== "") {
        values.add(value);
      }
    });
    return Array.from(values).sort();
  };

  // Excel-style column filter handlers
  const handleFilterClick = (event, field) => {
    setFilterAnchorEl(event.currentTarget);
    setActiveFilterColumn(field);
  };

  const handleFilterClose = () => {
    setFilterAnchorEl(null);
    setActiveFilterColumn(null);
  };

  const handleColumnFilterChange = (field, value) => {
    setColumnFilters((prev) => {
      const currentFilters = prev[field] || [];
      if (currentFilters.includes(value)) {
        const newFilters = currentFilters.filter((f) => f !== value);
        if (newFilters.length === 0) {
          const { [field]: removed, ...rest } = prev;
          return rest;
        }
        return { ...prev, [field]: newFilters };
      } else {
        return { ...prev, [field]: [...currentFilters, value] };
      }
    });
  };

  const handleClearColumnFilter = (field) => {
    const { [field]: removed, ...rest } = columnFilters;
    setColumnFilters(rest);
  };

  // Filter and search data
  const filteredData = useMemo(() => {
    if (!requestListRows || requestListRows.length === 0) return [];

    let filtered = [...requestListRows];

    // Apply search query
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(
        (item) =>
          item.employeeName?.toLowerCase().includes(query) ||
          item.department?.toLowerCase().includes(query) ||
          item.trainingName?.toLowerCase().includes(query) ||
          item.motivation?.toLowerCase().includes(query)
      );
    }

    // Apply Excel-style column filters
    Object.keys(columnFilters).forEach((field) => {
      const filterValues = columnFilters[field];
      if (filterValues && filterValues.length > 0) {
        filtered = filtered.filter((item) => {
          const value = item[field];
          return filterValues.includes(value);
        });
      }
    });

    return filtered;
  }, [requestListRows, searchQuery, columnFilters]);

  // Custom header renderer with filter icon
  const renderHeaderWithFilter = (field, headerName) => {
    const currentFilters = columnFilters[field] || [];
    const hasFilter = currentFilters.length > 0;

    return (
      <div className="flex items-center justify-between w-full pr-2">
        <span className="font-semibold">{headerName}</span>
        <div className="flex items-center">
          {/* Filter icon */}
          <IconButton
            size="small"
            onClick={(e) => handleFilterClick(e, field)}
            className="!p-1 !ml-2"
            sx={{
              color: hasFilter ? "#1976d2" : "#9e9e9e",
              "&:hover": {
                color: "#1976d2",
                backgroundColor: "rgba(25, 118, 210, 0.08)",
              },
            }}
          >
            <FilterListIcon sx={{ fontSize: 16 }} />
          </IconButton>
        </div>
      </div>
    );
  };

  // Fetch prompt for grouping
  const fetchGroupingPrompt = async () => {
    try {
      // First try to fetch from database
      const { data, error } = await supabase
        .from("prompt_master")
        .select("prompt")
        .eq("category", CHAT_CATEGORIES.GROUP_REQUEST_LIST.id)
        .eq("status", "true")
        .order("index", { ascending: true })
        .limit(1);

      if (!error && data && data.length > 0) {
        return data[0].prompt;
      }

      // Fallback to default prompt
      const defaultPrompt = DEFAULT_PROMPTS.find(
        (p) => p.category === CHAT_CATEGORIES.GROUP_REQUEST_LIST.id
      );
      return defaultPrompt?.prompt || "";
    } catch (error) {
      console.error("Error fetching grouping prompt:", error);
      // Fallback to default prompt
      const defaultPrompt = DEFAULT_PROMPTS.find(
        (p) => p.category === CHAT_CATEGORIES.GROUP_REQUEST_LIST.id
      );
      return defaultPrompt?.prompt || "";
    }
  };

  const handleAddTraining = async (row) => {
    // Navigate to Add Training page with category selection, similar to critical skills flow
    navigate("/add-training-catalogue", {
      state: {
        returnToRequestedCatalogue: true,
        activeTab: "CompanyCatalogue",
        rowData: row, // Store the row data to remove it after saving
        groupId: row.id, // Store group ID to identify which group this belongs to
      },
    });
  };

  // Handle returning from Add Training page
  useEffect(() => {
    if (location.state?.trainingAdded && location.state?.rowData && !trainingAddedHandledRef.current) {
      trainingAddedHandledRef.current = true; // Mark as handled to prevent multiple executions
      
      const handleTrainingAssigned = async () => {
        try {
          const groupRow = groupRequestListRows.find(
            (r) => r.id === location.state.rowData.id
          );

          // Get training request IDs from groupRow or from location.state.rowData
          const trainingRequestIds =
            groupRow?.trainingRequestIds ||
            location.state.rowData?.trainingRequestIds ||
            [];
          // Update the group: remove only the assigned training request IDs, not the entire group
          if (groupRow && trainingRequestIds.length > 0) {
            const assignedRequestIds = trainingRequestIds;
            const updatedRows = groupRequestListRows
              .map((row) => {
                if (row.id === location.state.rowData.id) {
                  // Remove assigned training request IDs from this group
                  const remainingRequestIds = (
                    row.trainingRequestIds || []
                  ).filter((id) => !assignedRequestIds.includes(id));

                  // Update count based on remaining requests
                  const newCount = remainingRequestIds.length;

                  // If no requests left, remove the group entirely
                  if (newCount === 0) {
                    return null; // Mark for removal
                  }

                  return {
                    ...row,
                    trainingRequestIds: remainingRequestIds,
                    count: newCount,
                  };
                }
                return row;
              })
              .filter(Boolean); // Remove null entries (empty groups)

            setGroupRequestListRows(updatedRows);

            // Update localStorage
            try {
              const localData = getLocalData();
              if (localData?.company_id) {
                const storageKey = `groupedTrainingRequests_${localData.company_id}`;
                localStorage.setItem(storageKey, JSON.stringify(updatedRows));
              }
            } catch (error) {
              console.error("Error updating localStorage:", error);
            }
          }

          // Show a simple success message (only once)
          toast.success("Training added successfully");
          
          // Clear location.state to prevent re-triggering
          navigate(location.pathname, { replace: true, state: {} });

          // Refresh the request list to show updated statuses
          const localData = getLocalData();
          if (localData?.company_id) {
            // Re-fetch training requests to get updated status
            const { data: updatedRequests, error: fetchError } =
              await fromWithCompany("training_requests")
                .select(
                  `
                *,
                company_user!user_id (
                  id,
                  first_name,
                  last_name,
                  department_id,
                  email
                )
              `
                )
                .in("status", ["requested", "processed", "rejected"]) // Include requested, processed, and rejected to show status
                .order("created_at", { ascending: false });

            if (!fetchError && updatedRequests) {
              setRawTrainingRequests(updatedRequests);
            } else if (fetchError) {
              console.error("Error fetching updated requests:", fetchError);
            }
          }

          // Clear location state to prevent re-running
          navigate(location.pathname, { replace: true, state: {} });
        } catch (error) {
          console.error("Error handling training assignment:", error);
          toast.error("Error refreshing data: " + error.message);
        }
      };

      handleTrainingAssigned();
    }
    
    // Reset the ref when location.state changes (new navigation)
    if (!location.state?.trainingAdded) {
      trainingAddedHandledRef.current = false;
    }
  }, [location.state?.trainingAdded, location.state?.rowData, groupRequestListRows, navigate, location.pathname]);

  // Handle reject request
  const handleRejectRequest = async (requestId) => {
    try {
      const localData = getLocalData();
      if (!localData?.company_id) {
        toast.error("Company ID not found");
        return;
      }

      // Update status to rejected
      const { error: updateError } = await supabase
        .from("training_requests")
        .update({
          status: "rejected",
          updated_at: new Date().toISOString(),
        })
        .eq("id", requestId)
        .eq("company_id", localData.company_id);

      if (updateError) {
        throw updateError;
      }

      toast.success("Training request rejected successfully");

      // Refresh the request list to show updated status
      const { data: updatedRequests, error: fetchError } =
        await fromWithCompany("training_requests")
          .select(
            `
            *,
            company_user!user_id (
              id,
              first_name,
              last_name,
              department_id,
              email
            )
          `
          )
          .in("status", ["requested", "processed", "rejected"])
          .order("created_at", { ascending: false });

      if (!fetchError && updatedRequests) {
        setRawTrainingRequests(updatedRequests);
      } else if (fetchError) {
        console.error("Error fetching updated requests:", fetchError);
      }
    } catch (error) {
      console.error("Error rejecting training request:", error);
      toast.error("Error rejecting training request: " + error.message);
    }
  };

  const handleDeleteClick = (row) => {
    setRowToDelete(row);
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = () => {
    if (rowToDelete) {
      // Remove the row from groupRequestListRows
      const updatedRows = groupRequestListRows.filter(
        (row) => row.id !== rowToDelete.id
      );
      setGroupRequestListRows(updatedRows);

      // Update localStorage
      try {
        const localData = getLocalData();
        if (localData?.company_id) {
          const storageKey = `groupedTrainingRequests_${localData.company_id}`;
          localStorage.setItem(storageKey, JSON.stringify(updatedRows));
        }
      } catch (error) {
        console.error("Error updating localStorage:", error);
      }

      toast.success("Grouped training request deleted successfully");
    }
    setDeleteDialogOpen(false);
    setRowToDelete(null);
  };

  const handleDeleteCancel = () => {
    setDeleteDialogOpen(false);
    setRowToDelete(null);
  };

  const handleCountClick = (row) => {
    // Always reconstruct learners from requestListRows to get the most up-to-date department information
    let learners = [];

    if (row.trainingRequestIds && row.trainingRequestIds.length > 0) {
      // Reconstruct from requestListRows to ensure we have the latest department data
      learners = requestListRows
        .filter((reqRow) => row.trainingRequestIds.includes(reqRow.id))
        .map((reqRow) => ({
          id: reqRow.id,
          employeeName: reqRow.employeeName,
          department: reqRow.department,
          trainingName: reqRow.trainingName,
        }));
    } else if (row.learners && row.learners.length > 0) {
      // Fallback to stored learners if trainingRequestIds not available
      learners = row.learners;
    }

    if (learners.length > 0) {
      setSelectedGroupLearners(learners);
      setLearnersDialogOpen(true);
    } else {
      toast.warning("No learners found for this group");
    }
  };

  const handleLearnersDialogClose = () => {
    setLearnersDialogOpen(false);
    setSelectedGroupLearners([]);
  };

  const handleGroupWithAI = async () => {
    // Filter only requested status requests for grouping
    const requestedRows = requestListRows.filter(
      (row) => row.status === "requested"
    );

    if (!requestedRows || requestedRows.length === 0) {
      toast.warning("No requested training requests available to group");
      return;
    }

    // Clear existing grouped list when clicking "Group with AI" again
    setGroupRequestListRows([]);
    const localData = getLocalData();
    if (localData?.company_id) {
      const storageKey = `groupedTrainingRequests_${localData.company_id}`;
      localStorage.removeItem(storageKey);
    }

    setGroupingWithAI(true);
    try {
      // Prepare training requests data for AI (include IDs to track which requests are in each group)
      // Only include requests with "requested" status
      const trainingRequestsData = requestedRows.map((row) => ({
        id: row.id, // Include ID - AI will return these IDs in trainingRequestIds array
        trainingName: row.trainingName,
        motivation: row.motivation,
        program_duration: row.program_duration,
        employeeName: row.employeeName,
        department: row.department,
      }));

      // Fetch the prompt
      const prompt = await fetchGroupingPrompt();

      if (!prompt) {
        toast.error("Grouping prompt not found");
        setGroupingWithAI(false);
        return;
      }

      // Prepare the message with only training requests data (prompt goes in prompt field)
      // Send as JSON string since backend expects string format
      const message = JSON.stringify(trainingRequestsData);

      const helperData = await postBackendHelper({
        prompt,
        message,
      });

      let groupedData = [];
      const aiResponse = helperData?.data?.content || helperData?.data;

      if (typeof aiResponse === "string") {
        // Try to parse JSON from string
        let cleanedResponse = aiResponse;
        if (cleanedResponse.includes("```json")) {
          cleanedResponse = cleanedResponse.replace(/```json|```/g, "").trim();
        }
        try {
          groupedData = JSON.parse(cleanedResponse);
        } catch (parseError) {
          console.error("Error parsing AI response:", parseError);
          toast.error("Failed to parse AI response. Please try again.");
          setGroupingWithAI(false);
          return;
        }
      } else if (Array.isArray(aiResponse)) {
        groupedData = aiResponse;
      } else {
        throw new Error("Invalid response format from AI");
      }

      // Format the grouped data for the table
      // Use trainingRequestIds directly from AI response
      // Create a map of request ID to request row for quick lookup
      const requestRowMap = new Map();
      requestedRows.forEach((row) => {
        requestRowMap.set(row.id, row);
      });

      const formattedGroupedData = groupedData.map((group, index) => {
        // Get training request IDs from AI response
        const trainingRequestIds = group.trainingRequestIds || [];

        const validRequestIds = trainingRequestIds.filter((id) =>
          requestRowMap.has(id)
        );

        // Get matching request rows for learner information
        const matchingRows = validRequestIds
          .map((id) => requestRowMap.get(id))
          .filter(Boolean);

        // Extract learner information
        const learners = matchingRows.map((row) => ({
          id: row.id,
          employeeName: row.employeeName,
          department: row.department,
          trainingName: row.trainingName,
        }));

        return {
          id: `group-${index}-${Date.now()}`, // Unique ID for each group
          count: validRequestIds.length, // Use count from valid request IDs
          topic: group.topic || "-",
          trainingName: group.trainingName || "-",
          motivation: group.motivation || "-",
          program_duration: group.program_duration || "-",
          // Store which training request IDs are part of this group (from AI response)
          trainingRequestIds: validRequestIds,
          // Store learner information for display
          learners: learners,
        };
      });

      setGroupRequestListRows(formattedGroupedData);

      // Store in localStorage (persists until logout)
      try {
        const localData = getLocalData();
        if (localData?.company_id) {
          const storageKey = `groupedTrainingRequests_${localData.company_id}`;
          localStorage.setItem(
            storageKey,
            JSON.stringify(formattedGroupedData)
          );
        }
      } catch (error) {
        console.error("Error saving grouped results to storage:", error);
        // Continue even if storage fails
      }

      // Switch to Grouped Requests tab
      setActiveTab("Grouped Requests");

      toast.success("Training requests grouped successfully!");
    } catch (error) {
      console.error("Error grouping with AI:", error);
      toast.error(
        error.response?.data?.error ||
          error.message ||
          "Failed to group training requests. Please try again."
      );
    } finally {
      setGroupingWithAI(false);
    }
  };

  return (
    <div className="h-[calc(100vh-116px)] relative flex flex-col overflow-hidden">
      <div className="flex-shrink-0">
        <h2 className="!font-[Poppins] xl:text-3xl xl:leading-3xl text-2xl leading-2xl text-blue-500 font-semibold 2xl:mb-4 md:mb-3 mb-4">
          Training Not in Catalogue
        </h2>
      </div>

      {/* Instructions Section */}
      <div className="border-2 border-blue-500 bg-white rounded-lg p-4 md:mb-6 mb-4 relative flex-shrink-0">
        <p className="bg-white text-lg font-semibold text-gray-700 mb-4 2xl:text-xl 2xl:leading-xl lg:text-base lg:leading-base text-sm leading-sm absolute md:top-[-16px] top-[-12px] left-[16px] px-1">
          Instructions
        </p>
        <div>
          <p className="font-medium mb-0 2xl:text-base text-sm 2xl:leading-base leading-sm text-gray-300">
            View and manage training requests submitted by employees. Use the
            "Requests" tab to see individual training requests with their status
            (Requested, Processed, or Rejected). Click "Group with AI" to
            automatically group similar training requests together. In the
            "Grouped Requests" tab, you can view grouped requests, see the
            number of learners, add trainings to your catalogue, or delete
            grouped requests. Use the search bar to filter requests by employee
            name, department, training name, or motivation.
          </p>
        </div>
      </div>

      <div className="bg-white border border-solid border-blue-200 rounded-xl overflow-hidden p-4 flex-1 min-h-0 flex flex-col">
        {/* Tabs */}
        <div className="mb-4">
          <CTabs tabs={tabs} value={activeTab} onChange={setActiveTab} />
        </div>

        {loading ? (
          <div className="flex justify-center items-center flex-1">
            <CLoader />
          </div>
        ) : (
          <div className="flex-1 min-h-0 flex flex-col">
            {/* Requests Tab */}
            <CTabPanel
              value={activeTab}
              index="Requests"
              className="flex-1 min-h-0 flex flex-col"
            >
              <div className="bg-white border border-solid border-blue-200 rounded-xl overflow-hidden flex flex-col flex-1 min-h-0">
                <div className="flex items-center justify-between p-3 gap-3 flex-shrink-0">
                  <CTextField
                    name="search"
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    startIcon={<SearchIcon className="text-blue-300" />}
                    placeholder={"Search"}
                    className="!mt-0 w-full 2xl:max-w-[400px] max-w-[300px]"
                  />
                  <div className="flex items-center">
                    <CButton
                      className="text-white gradient-btn rounded-lg p-0 ml-3"
                      onClick={handleGroupWithAI}
                      disabled={groupingWithAI || requestListRows.length === 0}
                    >
                      {groupingWithAI ? "Grouping..." : "Group with AI"}
                    </CButton>
                  </div>
                </div>
                {/* Data Table */}
                <div className="w-full flex-1 min-h-0">
                  <CDataGrid
                    columns={requestListColumns}
                    rows={filteredData}
                    searchQuery={searchQuery}
                    idKey="id"
                    className="bg-white shadow-md rounded-lg"
                  />
                </div>
                {/* Filter Popover */}
                <Popover
                  open={Boolean(filterAnchorEl)}
                  anchorEl={filterAnchorEl}
                  onClose={handleFilterClose}
                  anchorOrigin={{
                    vertical: "bottom",
                    horizontal: "left",
                  }}
                  transformOrigin={{
                    vertical: "top",
                    horizontal: "left",
                  }}
                >
                  <Box
                    sx={{
                      p: 2,
                      minWidth: 200,
                      maxWidth: 300,
                      maxHeight: 400,
                      overflowY: "auto",
                    }}
                  >
                    <div className="flex items-center justify-between mb-3">
                      <span className="font-semibold text-sm">
                        Filter {activeFilterColumn}
                      </span>
                      <CButton
                        onClick={() =>
                          handleClearColumnFilter(activeFilterColumn)
                        }
                        className="!text-xs !h-auto !px-2 !py-1 !min-w-0"
                      >
                        Clear
                      </CButton>
                    </div>
                    {activeFilterColumn && (
                      <div>
                        {getUniqueColumnValues(activeFilterColumn).map(
                          (value) => {
                            // For status, show display labels
                            let displayValue = value;
                            if (activeFilterColumn === "status") {
                              if (value === "processed")
                                displayValue = "Processed";
                              else if (value === "rejected")
                                displayValue = "Rejected";
                              else if (value === "requested")
                                displayValue = "Requested";
                            } else if (
                              activeFilterColumn === "program_duration"
                            ) {
                              // Map program_duration enum values to display labels
                              const option = PROGRAM_DURATION_OPTIONS.find(
                                (opt) => opt.id === value
                              );
                              displayValue = option ? option.value : value;
                            }
                            return (
                              <FormControlLabel
                                key={value}
                                control={
                                  <Checkbox
                                    checked={(
                                      columnFilters[activeFilterColumn] || []
                                    ).includes(value)}
                                    onChange={() =>
                                      handleColumnFilterChange(
                                        activeFilterColumn,
                                        value
                                      )
                                    }
                                  />
                                }
                                label={
                                  <span className="text-sm">
                                    {displayValue || "(Blank)"}
                                  </span>
                                }
                                className="block mb-1"
                              />
                            );
                          }
                        )}
                      </div>
                    )}
                  </Box>
                </Popover>
              </div>
            </CTabPanel>

            {/* Grouped Requests Tab */}
            <CTabPanel
              value={activeTab}
              index="Grouped Requests"
              className="flex-1 min-h-0 flex flex-col"
            >
              <div className="bg-white border border-solid border-blue-200 rounded-xl overflow-hidden flex flex-col flex-1 min-h-0">
                <div className="flex items-center justify-between p-3 flex-shrink-0">
                  <CTextField
                    name="search"
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    startIcon={<SearchIcon className="text-blue-300" />}
                    placeholder={"Search"}
                    className="!mt-0 w-full 2xl:max-w-[400px] max-w-[300px]"
                  />
                </div>
                {/* Data Table */}
                <div className="w-full flex-1 min-h-0">
                  {groupingWithAI ? (
                    <div className="flex justify-center items-center h-full">
                      <CLoader />
                    </div>
                  ) : groupRequestListRows.length > 0 ? (
                    <CDataGrid
                      columns={groupRequestListColumns}
                      rows={groupRequestListRows}
                      searchQuery={searchQuery}
                      idKey="id"
                      className="bg-white shadow-md rounded-lg"
                    />
                  ) : (
                    <div className="flex flex-col justify-center items-center h-full text-gray-500">
                      <p className="text-lg font-medium mb-2">
                        No grouped data available
                      </p>
                      <p className="text-sm">
                        Click "Group with AI" in the Requests tab to group
                        training requests
                      </p>
                    </div>
                  )}
                </div>
              </div>
            </CTabPanel>
          </div>
        )}
      </div>

      {/* Delete Confirmation Dialog */}
      <ConfirmationDialog
        open={deleteDialogOpen}
        onClose={handleDeleteCancel}
        onConfirm={handleDeleteConfirm}
        title="Delete Grouped Training Request"
        description={`Are you sure you want to delete the grouped training request for "${
          rowToDelete?.trainingName || ""
        }"? This action cannot be undone.`}
        confirmText="Delete"
        cancelText="Cancel"
        confirmClass="bg-red-500 text-white hover:bg-red-700 border border-red-500 hover:border-red-700 px-4 py-2 rounded-lg"
      />

      {/* Learners List Dialog */}
      <CDialog
        open={learnersDialogOpen}
        onClose={handleLearnersDialogClose}
        maxWidth="md"
        fullWidth
      >
        <div className="p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-xl font-semibold text-blue-500">
              Learners in Group ({selectedGroupLearners.length})
            </h3>
            <CButton
              onClick={handleLearnersDialogClose}
              className="!h-auto !p-0 !min-w-0"
            >
              <CloseIcon className="text-gray-700 hover:text-gray-900" />
            </CButton>
          </div>

          {selectedGroupLearners.length > 0 ? (
            <div className="border border-blue-200 rounded-xl overflow-hidden">
              <table className="w-full border-collapse">
                <thead>
                  <tr className="bg-blue-100">
                    <th className="text-left p-3 font-semibold text-gray-700 border-b border-blue-200">
                      #
                    </th>
                    <th className="text-left p-3 font-semibold text-gray-700 border-b border-blue-200">
                      Employee Name
                    </th>
                    <th className="text-left p-3 font-semibold text-gray-700 border-b border-blue-200">
                      Department
                    </th>
                    <th className="text-left p-3 font-semibold text-gray-700 border-b border-blue-200">
                      Training Name
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {selectedGroupLearners.map((learner, index) => (
                    <tr
                      key={learner.id}
                      className="border-b border-blue-200 hover:bg-blue-50"
                    >
                      <td className="p-3 text-gray-700">{index + 1}</td>
                      <td className="p-3 text-gray-700">
                        {learner.employeeName || "-"}
                      </td>
                      <td className="p-3 text-gray-700">
                        {learner.department || "-"}
                      </td>
                      <td className="p-3 text-gray-700">
                        {learner.trainingName || "-"}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <div className="text-center py-8 text-gray-500">
              No learners found in this group.
            </div>
          )}
        </div>
      </CDialog>
    </div>
  );
};

export default RequestedCatalogue;
